import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

// ── Helpers ───────────────────────────────────────────────────────────────────
function authHeader() {
  const token = localStorage.getItem("admin_token") || "";
  return { Authorization: `Bearer ${token}` };
}

const STATUS_COLORS = {
  upcoming: { bg: "#2a1f00", border: "#f59e0b", text: "#fbbf24" },
  active:   { bg: "#052e16", border: "#16a34a", text: "#4ade80" },
  finished: { bg: "#0c1a2e", border: "#2563eb", text: "#60a5fa" },
};

// ── Toast notification ────────────────────────────────────────────────────────
function Toast({ message, type, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div style={{
      position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 1000,
      background: type === "error" ? "#450a0a" : "#052e16",
      border: `1px solid ${type === "error" ? "#ef4444" : "#16a34a"}`,
      color: type === "error" ? "#fca5a5" : "#86efac",
      padding: "0.75rem 1.25rem", borderRadius: "8px",
      fontSize: "0.875rem", fontWeight: "500",
      boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
      animation: "slideIn 0.2s ease",
    }}>
      {type === "error" ? "✗ " : "✓ "}{message}
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "12px",
        padding: "2rem", maxWidth: "420px", width: "90%",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        <div style={{ fontSize: "1.5rem", marginBottom: "0.75rem" }}>⚠️</div>
        <p style={{ color: "#e5e7eb", marginBottom: "1.5rem", lineHeight: "1.5" }}>{message}</p>
        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{
            padding: "0.5rem 1.25rem", borderRadius: "6px", border: "1px solid #374151",
            background: "transparent", color: "#9ca3af", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            padding: "0.5rem 1.25rem", borderRadius: "6px", border: "none",
            background: "#dc2626", color: "#fff", cursor: "pointer", fontWeight: "600",
          }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

// ── Season Form Modal ─────────────────────────────────────────────────────────
function SeasonModal({ season, onSave, onClose }) {
  const isEdit = !!season?.id;
  const toDatetimeLocal = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const [form, setForm] = useState({
    name: season?.name || "",
    description: season?.description || "",
    status: season?.status || "upcoming",
    auto_assign_from: toDatetimeLocal(season?.auto_assign_from),
    auto_assign_to:   toDatetimeLocal(season?.auto_assign_to),
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        auto_assign_from: form.auto_assign_from || null,
        auto_assign_to:   form.auto_assign_to   || null,
      };
      if (isEdit) {
        const { data } = await axios.patch(`${API}/api/seasons/${season.id}`, payload, { headers: authHeader() });
        onSave(data.season, "Season updated");
      } else {
        const { data } = await axios.post(`${API}/api/seasons`, payload, { headers: authHeader() });
        onSave(data.season, "Season created");
      }
    } catch (err) {
      onSave(null, err.response?.data?.error || "Failed to save season");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", background: "#13151f", border: "1px solid #2d3148",
    borderRadius: "6px", padding: "0.6rem 0.8rem", color: "#e5e7eb",
    fontSize: "0.9rem", outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "12px",
        padding: "2rem", width: "90%", maxWidth: "500px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <h2 style={{ margin: 0, color: "#f3f4f6" }}>{isEdit ? "Edit Season" : "Create Season"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "1.5rem", lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Season Name *
            </label>
            <input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Season 1 — Spring 2026"
              required
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Description
            </label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Optional details about format, rules, teams..."
              rows={3}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Auto-Assign Date Range
              <span style={{ color: "#6b7280", fontWeight: "400", marginLeft: "0.4rem" }}>
                — matches played between these dates will automatically join this season
              </span>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
              <div>
                <div style={{ color: "#6b7280", fontSize: "0.75rem", marginBottom: "0.25rem" }}>From</div>
                <input
                  type="datetime-local"
                  value={form.auto_assign_from}
                  onChange={e => setForm(f => ({ ...f, auto_assign_from: e.target.value }))}
                  style={{ ...inputStyle, colorScheme: "dark" }}
                />
              </div>
              <div>
                <div style={{ color: "#6b7280", fontSize: "0.75rem", marginBottom: "0.25rem" }}>To</div>
                <input
                  type="datetime-local"
                  value={form.auto_assign_to}
                  onChange={e => setForm(f => ({ ...f, auto_assign_to: e.target.value }))}
                  style={{ ...inputStyle, colorScheme: "dark" }}
                />
              </div>
            </div>
            {form.auto_assign_from && form.auto_assign_to && (
              <div style={{ marginTop: "0.4rem", fontSize: "0.75rem", color: "#4ade80" }}>
                ✓ Matches played between {new Date(form.auto_assign_from).toLocaleString()} and {new Date(form.auto_assign_to).toLocaleString()} will auto-join this season
              </div>
            )}
            {(form.auto_assign_from || form.auto_assign_to) && (
              <button type="button"
                onClick={() => setForm(f => ({ ...f, auto_assign_from: "", auto_assign_to: "" }))}
                style={{ marginTop: "0.4rem", background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "0.75rem", padding: 0 }}>
                × Clear date range
              </button>
            )}
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Status
            </label>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              {["upcoming", "active", "finished"].map(s => {
                const c = STATUS_COLORS[s];
                const active = form.status === s;
                return (
                  <button key={s} type="button"
                    onClick={() => setForm(f => ({ ...f, status: s }))}
                    style={{
                      flex: 1, padding: "0.5rem", borderRadius: "6px", cursor: "pointer",
                      border: `1px solid ${active ? c.border : "#2d3148"}`,
                      background: active ? c.bg : "transparent",
                      color: active ? c.text : "#6b7280",
                      fontWeight: active ? "600" : "400",
                      fontSize: "0.85rem", transition: "all 0.15s",
                    }}>
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{
              padding: "0.55rem 1.25rem", borderRadius: "6px",
              border: "1px solid #374151", background: "transparent",
              color: "#9ca3af", cursor: "pointer",
            }}>Cancel</button>
            <button type="submit" disabled={saving} style={{
              padding: "0.55rem 1.5rem", borderRadius: "6px", border: "none",
              background: "#f0a500", color: "#0f1117",
              fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}>
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Season"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Assign Match Modal ────────────────────────────────────────────────────────
function AssignMatchModal({ season, onClose, onDone }) {
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/matches?limit=200`)
      .then(r => setMatches(r.data.matches || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const unassigned = matches.filter(m =>
    !m.season_id &&
    (m.map?.toLowerCase().includes(search.toLowerCase()) ||
     m.team1_name?.toLowerCase().includes(search.toLowerCase()) ||
     m.team2_name?.toLowerCase().includes(search.toLowerCase()))
  );

  const toggle = (id) => setSelected(s =>
    s.includes(id) ? s.filter(x => x !== id) : [...s, id]
  );

  const handleAssign = async () => {
    if (!selected.length) return;
    setSaving(true);
    try {
      await Promise.all(selected.map(match_id =>
        axios.post(`${API}/api/seasons/${season.id}/assign-match`,
          { match_id }, { headers: authHeader() })
      ));
      onDone(`Assigned ${selected.length} match${selected.length > 1 ? "es" : ""}`);
    } catch (err) {
      onDone(null, err.response?.data?.error || "Failed to assign matches");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "12px",
        padding: "2rem", width: "90%", maxWidth: "600px", maxHeight: "80vh",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h2 style={{ margin: 0, color: "#f3f4f6" }}>Assign Matches — {season.name}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "1.5rem", lineHeight: 1 }}>×</button>
        </div>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter by map or team name..."
          style={{
            background: "#13151f", border: "1px solid #2d3148", borderRadius: "6px",
            padding: "0.6rem 0.8rem", color: "#e5e7eb", fontSize: "0.9rem",
            outline: "none", marginBottom: "0.75rem",
          }}
        />

        <div style={{ overflowY: "auto", flex: 1, marginBottom: "1rem" }}>
          {loading ? (
            <p style={{ color: "#9ca3af", textAlign: "center", padding: "2rem" }}>Loading matches...</p>
          ) : unassigned.length === 0 ? (
            <p style={{ color: "#9ca3af", textAlign: "center", padding: "2rem" }}>
              No unassigned matches found.
            </p>
          ) : (
            unassigned.map(m => {
              const isSelected = selected.includes(m.id);
              return (
                <div key={m.id} onClick={() => toggle(m.id)} style={{
                  display: "flex", alignItems: "center", gap: "0.75rem",
                  padding: "0.65rem 0.75rem", borderRadius: "6px", cursor: "pointer",
                  background: isSelected ? "rgba(240,165,0,0.1)" : "transparent",
                  border: `1px solid ${isSelected ? "#f0a500" : "transparent"}`,
                  marginBottom: "0.3rem", transition: "all 0.1s",
                }}>
                  <div style={{
                    width: 18, height: 18, borderRadius: "4px", flexShrink: 0,
                    border: `2px solid ${isSelected ? "#f0a500" : "#374151"}`,
                    background: isSelected ? "#f0a500" : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isSelected && <span style={{ color: "#0f1117", fontSize: "0.7rem", fontWeight: "900" }}>✓</span>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                      <span style={{ fontWeight: "600", color: "#e5e7eb", fontSize: "0.9rem" }}>{m.map}</span>
                      <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>#{m.id}</span>
                    </div>
                    <div style={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                      {m.team1_name} {m.team1_score}–{m.team2_score} {m.team2_name}
                    </div>
                  </div>
                  <div style={{ color: "#6b7280", fontSize: "0.75rem" }}>
                    {m.finished_at ? new Date(m.finished_at).toLocaleDateString() : "—"}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ color: "#9ca3af", fontSize: "0.85rem" }}>
            {selected.length} match{selected.length !== 1 ? "es" : ""} selected
          </span>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            <button onClick={onClose} style={{
              padding: "0.5rem 1.25rem", borderRadius: "6px",
              border: "1px solid #374151", background: "transparent",
              color: "#9ca3af", cursor: "pointer",
            }}>Cancel</button>
            <button onClick={handleAssign} disabled={!selected.length || saving} style={{
              padding: "0.5rem 1.25rem", borderRadius: "6px", border: "none",
              background: selected.length ? "#f0a500" : "#374151",
              color: selected.length ? "#0f1117" : "#6b7280",
              fontWeight: "600", cursor: selected.length && !saving ? "pointer" : "not-allowed",
            }}>
              {saving ? "Assigning..." : "Assign Selected"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit Match Modal ─────────────────────────────────────────────────────────
function EditMatchModal({ match, onSave, onClose }) {
  const [form, setForm] = useState({
    team1_name: match.team1_name || "",
    team2_name: match.team2_name || "",
    map: match.map || "",
  });
  const [saving, setSaving] = useState(false);

  const inputStyle = {
    width: "100%", background: "#13151f", border: "1px solid #2d3148",
    borderRadius: "6px", padding: "0.6rem 0.8rem", color: "#e5e7eb",
    fontSize: "0.9rem", outline: "none", boxSizing: "border-box",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.patch(`${API}/api/matches/${match.id}`, form, { headers: authHeader() });
      onSave(`Match #${match.id} updated`);
    } catch (err) {
      onSave(null, err.response?.data?.error || "Failed to update match");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "12px",
        padding: "2rem", width: "90%", maxWidth: "460px",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
          <div>
            <h2 style={{ margin: 0, color: "#f3f4f6" }}>Edit Match</h2>
            <div style={{ color: "#6b7280", fontSize: "0.8rem", marginTop: "0.2rem" }}>
              #{match.id} · {match.map} · {match.team1_score}–{match.team2_score}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "1.5rem", lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Team 1 Name
            </label>
            <input
              value={form.team1_name}
              onChange={e => setForm(f => ({ ...f, team1_name: e.target.value }))}
              placeholder="Team 1"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: "1rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Team 2 Name
            </label>
            <input
              value={form.team2_name}
              onChange={e => setForm(f => ({ ...f, team2_name: e.target.value }))}
              placeholder="Team 2"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: "1.5rem" }}>
            <label style={{ display: "block", color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>
              Map
            </label>
            <input
              value={form.map}
              onChange={e => setForm(f => ({ ...f, map: e.target.value }))}
              placeholder="de_dust2"
              style={inputStyle}
            />
          </div>

          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={{
              padding: "0.55rem 1.25rem", borderRadius: "6px",
              border: "1px solid #374151", background: "transparent",
              color: "#9ca3af", cursor: "pointer",
            }}>Cancel</button>
            <button type="submit" disabled={saving} style={{
              padding: "0.55rem 1.5rem", borderRadius: "6px", border: "none",
              background: "#f0a500", color: "#0f1117",
              fontWeight: "700", cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1,
            }}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Season Card ───────────────────────────────────────────────────────────────
function SeasonCard({ season, onEdit, onDelete, onAssign, onUnassign }) {
  const c = STATUS_COLORS[season.status] || STATUS_COLORS.upcoming;
  const [expanded, setExpanded] = useState(false);
  const [assignedMatches, setAssignedMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [editMatch, setEditMatch] = useState(null);

  const loadMatches = async () => {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    setLoadingMatches(true);
    try {
      const { data } = await axios.get(`${API}/api/seasons/${season.id}`);
      setAssignedMatches(data.matches || []);
    } catch {}
    finally { setLoadingMatches(false); }
  };

  const handleUnassign = async (matchId) => {
    try {
      await axios.patch(`${API}/api/matches/${matchId}`,
        { season_id: null }, { headers: authHeader() });
      setAssignedMatches(m => m.filter(x => x.id !== matchId));
      onUnassign?.("Match removed from season");
    } catch (err) {
      onUnassign?.(null, err.response?.data?.error || "Failed to remove match");
    }
  };

  return (
    <div style={{
      background: "#1a1d26", border: `1px solid #2d3148`,
      borderLeft: `4px solid ${c.border}`,
      borderRadius: "10px", overflow: "hidden",
      transition: "border-color 0.2s",
    }}>
      {/* Header */}
      <div style={{ padding: "1.25rem 1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "0.3rem" }}>
              <h3 style={{ margin: 0, color: "#f3f4f6", fontSize: "1.05rem" }}>{season.name}</h3>
              <span style={{
                background: c.bg, color: c.text, border: `1px solid ${c.border}`,
                padding: "0.1rem 0.5rem", borderRadius: "4px", fontSize: "0.7rem", fontWeight: "700",
              }}>
                {season.status.toUpperCase()}
              </span>
            </div>
            {season.description && (
              <p style={{ margin: "0 0 0.5rem", color: "#9ca3af", fontSize: "0.85rem" }}>
                {season.description}
              </p>
            )}
            <div style={{ display: "flex", gap: "1.25rem", fontSize: "0.78rem", color: "#6b7280", flexWrap: "wrap" }}>
              <span>ID: {season.id}</span>
              <span>{season.match_count || 0} matches</span>
              {season.started_at && <span>Started {new Date(season.started_at).toLocaleDateString()}</span>}
              {season.finished_at && <span>Ended {new Date(season.finished_at).toLocaleDateString()}</span>}
            </div>
            {season.auto_assign_from && season.auto_assign_to && (
              <div style={{ marginTop: "0.35rem", fontSize: "0.75rem", color: "#4ade80" }}>
                🗓 Auto-assign: {new Date(season.auto_assign_from).toLocaleString()} → {new Date(season.auto_assign_to).toLocaleString()}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: "0.4rem", flexShrink: 0 }}>
            <Link to={`/seasons/${season.id}`} title="View public page"
              style={{ padding: "0.4rem 0.7rem", borderRadius: "5px", background: "#2d3148", color: "#e5e7eb", textDecoration: "none", fontSize: "0.8rem" }}>
              👁 View
            </Link>
            <button onClick={() => onAssign(season)} title="Assign matches"
              style={{ padding: "0.4rem 0.7rem", borderRadius: "5px", border: "none", background: "#2d3148", color: "#e5e7eb", cursor: "pointer", fontSize: "0.8rem" }}>
              ＋ Match
            </button>
            <button onClick={() => onEdit(season)} title="Edit season"
              style={{ padding: "0.4rem 0.7rem", borderRadius: "5px", border: "none", background: "#2d3148", color: "#e5e7eb", cursor: "pointer", fontSize: "0.8rem" }}>
              ✏ Edit
            </button>
            <button onClick={() => onDelete(season)} title="Delete season"
              style={{ padding: "0.4rem 0.7rem", borderRadius: "5px", border: "none", background: "#3b0c0c", color: "#fca5a5", cursor: "pointer", fontSize: "0.8rem" }}>
              🗑
            </button>
          </div>
        </div>

        {/* Expand matches */}
        <button onClick={loadMatches} style={{
          marginTop: "0.75rem", background: "none", border: "none",
          color: "#5865f2", cursor: "pointer", fontSize: "0.8rem", padding: 0,
        }}>
          {expanded ? "▲ Hide matches" : "▼ Show assigned matches"}
        </button>
      </div>

      {/* Assigned matches list */}
      {expanded && (
        <div style={{ borderTop: "1px solid #2d3148", padding: "0 1.5rem 1rem" }}>
          {loadingMatches ? (
            <p style={{ color: "#9ca3af", fontSize: "0.85rem", padding: "0.75rem 0" }}>Loading...</p>
          ) : assignedMatches.length === 0 ? (
            <p style={{ color: "#6b7280", fontSize: "0.85rem", padding: "0.75rem 0" }}>No matches assigned yet.</p>
          ) : (
            <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
              {assignedMatches.map(m => (
                <div key={m.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "0.5rem 0.75rem", borderRadius: "6px", background: "#13151f",
                }}>
                  <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                    <span style={{ color: "#f0a500", fontWeight: "600", fontSize: "0.85rem" }}>{m.map}</span>
                    <span style={{ color: "#9ca3af", fontSize: "0.82rem" }}>
                      {m.team1_name} {m.team1_score}–{m.team2_score} {m.team2_name}
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                    <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>
                      {m.finished_at ? new Date(m.finished_at).toLocaleDateString() : "—"}
                    </span>
                    <button onClick={() => handleUnassign(m.id)} title="Remove from season"
                      style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: "0.9rem", padding: "0 0.2rem" }}>
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {editMatch && (
        <EditMatchModal
          match={editMatch}
          onClose={() => setEditMatch(null)}
          onSave={(msg, err) => {
            setEditMatch(null);
            if (err) {
              onUnassign?.(null, err);
            } else {
              // Update local state so name change reflects immediately
              setAssignedMatches(ms => ms.map(m =>
                m.id === editMatch.id
                  ? { ...m, ...editMatch, team1_name: editMatch.team1_name, team2_name: editMatch.team2_name }
                  : m
              ));
              onUnassign?.(msg);
              // Re-fetch to get fresh data
              loadMatches().catch(() => {});
            }
          }}
        />
      )}
    </div>
  );
}

// ── Main Admin Page ───────────────────────────────────────────────────────────
export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [authed, setAuthed] = useState(!!localStorage.getItem("admin_token"));
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("seasons"); // seasons | matches
  const [allMatches, setAllMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchSearch, setMatchSearch] = useState("");
  const [editingMatch, setEditingMatch] = useState(null);

  const showToast = (message, type = "success") => setToast({ message, type });

  const loadAllMatches = useCallback(async () => {
    setMatchesLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/matches?limit=200`);
      setAllMatches(data.matches || []);
    } catch {}
    finally { setMatchesLoading(false); }
  }, []);

  const loadSeasons = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/seasons`);
      setSeasons(data.seasons || []);
    } catch (err) {
      showToast("Failed to load seasons", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) loadSeasons();
  }, [authed, loadSeasons]);

  useEffect(() => {
    if (authed && tab === "matches") loadAllMatches();
  }, [authed, tab, loadAllMatches]);

  const handleLogin = (e) => {
    e.preventDefault();
    localStorage.setItem("admin_token", tokenInput);
    setToken(tokenInput);
    setAuthed(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken(""); setAuthed(false); setSeasons([]);
  };

  const handleSave = async (season, messageOrError) => {
    setModal(null);
    if (!season) {
      showToast(messageOrError, "error");
    } else {
      showToast(messageOrError);
      await loadSeasons();
    }
  };

  const handleDelete = async (season) => {
    setModal({ type: "confirm", data: season });
  };

  const confirmDelete = async () => {
    const season = modal.data;
    setModal(null);
    try {
      await axios.delete(`${API}/api/seasons/${season.id}`, { headers: authHeader() });
      showToast(`"${season.name}" deleted`);
      setSeasons(s => s.filter(x => x.id !== season.id));
    } catch (err) {
      showToast(err.response?.data?.error || "Failed to delete season", "error");
    }
  };

  const handleAssignDone = async (message, error) => {
    setModal(null);
    if (error) showToast(error, "error");
    else { showToast(message); await loadSeasons(); }
  };

  const filteredSeasons = filter === "all" ? seasons : seasons.filter(s => s.status === filter);

  // ── Login screen ────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div style={{ maxWidth: "380px", margin: "6rem auto", padding: "0 1rem" }}>
        <div style={{
          background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "12px",
          padding: "2.5rem",
        }}>
          <div style={{ textAlign: "center", marginBottom: "2rem" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🔐</div>
            <h2 style={{ margin: 0, color: "#f3f4f6" }}>Admin Panel</h2>
            <p style={{ color: "#6b7280", fontSize: "0.85rem", marginTop: "0.4rem" }}>
              Enter your admin token to continue
            </p>
          </div>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              placeholder="Admin token"
              style={{
                width: "100%", background: "#13151f", border: "1px solid #2d3148",
                borderRadius: "6px", padding: "0.7rem 0.9rem", color: "#e5e7eb",
                fontSize: "0.9rem", outline: "none", boxSizing: "border-box",
                marginBottom: "1rem",
              }}
            />
            <button type="submit" style={{
              width: "100%", padding: "0.7rem", borderRadius: "6px", border: "none",
              background: "#f0a500", color: "#0f1117", fontWeight: "700",
              fontSize: "0.95rem", cursor: "pointer",
            }}>
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Admin dashboard ─────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`
        @keyframes slideIn {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ marginBottom: "0.2rem" }}>⚙️ Admin Panel</h1>
          <p style={{ color: "#6b7280", fontSize: "0.85rem", margin: 0 }}>Seasons & League Management</p>
        </div>
        <button onClick={handleLogout} style={{
          padding: "0.4rem 1rem", borderRadius: "6px", border: "1px solid #374151",
          background: "transparent", color: "#9ca3af", cursor: "pointer", fontSize: "0.85rem",
        }}>
          Sign Out
        </button>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1.5rem" }}>
        {[["seasons", "🏆 Seasons"], ["matches", "🎮 Matches"]].map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: "0.45rem 1.1rem", borderRadius: "6px", border: "none",
            cursor: "pointer", fontWeight: tab === t ? "700" : "400",
            background: tab === t ? "#f0a500" : "#2d3148",
            color: tab === t ? "#0f1117" : "#9ca3af",
          }}>{label}</button>
        ))}
      </div>

      {/* Stats bar */}
      <div className="stat-grid" style={{ marginBottom: "1.5rem" }}>
        <div className="stat-box">
          <div className="value">{seasons.length}</div>
          <div className="label">Total Seasons</div>
        </div>
        <div className="stat-box">
          <div className="value">{seasons.filter(s => s.status === "active").length}</div>
          <div className="label">Active</div>
        </div>
        <div className="stat-box">
          <div className="value">{seasons.reduce((a, s) => a + (parseInt(s.match_count) || 0), 0)}</div>
          <div className="label">Total Matches Assigned</div>
        </div>
        <div className="stat-box">
          <div className="value">{seasons.filter(s => s.status === "finished").length}</div>
          <div className="label">Completed</div>
        </div>
      </div>

      {tab === "matches" && (
        <div>
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "1rem", alignItems: "center" }}>
            <input
              value={matchSearch}
              onChange={e => setMatchSearch(e.target.value)}
              placeholder="Search by map or team name..."
              style={{
                flex: 1, background: "#13151f", border: "1px solid #2d3148",
                borderRadius: "6px", padding: "0.6rem 0.8rem", color: "#e5e7eb",
                fontSize: "0.9rem", outline: "none",
              }}
            />
            <button onClick={loadAllMatches} style={{
              padding: "0.6rem 1rem", borderRadius: "6px", border: "none",
              background: "#2d3148", color: "#e5e7eb", cursor: "pointer", fontSize: "0.85rem",
            }}>↻ Refresh</button>
          </div>

          {matchesLoading ? (
            <div className="card" style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>Loading matches...</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              {allMatches
                .filter(m =>
                  !matchSearch ||
                  m.map?.toLowerCase().includes(matchSearch.toLowerCase()) ||
                  m.team1_name?.toLowerCase().includes(matchSearch.toLowerCase()) ||
                  m.team2_name?.toLowerCase().includes(matchSearch.toLowerCase())
                )
                .map(m => (
                  <div key={m.id} style={{
                    background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "8px",
                    padding: "0.75rem 1rem", display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: "1rem",
                  }}>
                    <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flex: 1 }}>
                      <span style={{ color: "#f0a500", fontWeight: "600", fontSize: "0.9rem", minWidth: "80px" }}>{m.map}</span>
                      <span style={{ color: "#e5e7eb", fontSize: "0.9rem" }}>
                        {m.team1_name} <span style={{ color: "#f0a500" }}>{m.team1_score}–{m.team2_score}</span> {m.team2_name}
                      </span>
                      {m.season_id && (
                        <span style={{ background: "#1e3a5f", color: "#60a5fa", padding: "0.1rem 0.4rem", borderRadius: "3px", fontSize: "0.7rem" }}>
                          Season {m.season_id}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}>
                      <span style={{ color: "#6b7280", fontSize: "0.75rem" }}>
                        {m.finished_at ? new Date(m.finished_at).toLocaleDateString() : "—"}
                      </span>
                      <button onClick={() => setEditingMatch(m)} style={{
                        padding: "0.3rem 0.7rem", borderRadius: "5px", border: "none",
                        background: "#2d3148", color: "#e5e7eb", cursor: "pointer", fontSize: "0.8rem",
                      }}>✏ Edit</button>
                    </div>
                  </div>
                ))
              }
            </div>
          )}

          {editingMatch && (
            <EditMatchModal
              match={editingMatch}
              onClose={() => setEditingMatch(null)}
              onSave={(msg, err) => {
                setEditingMatch(null);
                if (err) showToast(err, "error");
                else { showToast(msg); loadAllMatches(); }
              }}
            />
          )}
        </div>
      )}

      {tab === "seasons" && <>
      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", flexWrap: "wrap", gap: "0.75rem" }}>
        <div style={{ display: "flex", gap: "0.4rem" }}>
          {["all", "upcoming", "active", "finished"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              padding: "0.35rem 0.9rem", borderRadius: "5px", border: "none",
              cursor: "pointer", fontSize: "0.82rem",
              background: filter === f ? "#f0a500" : "#2d3148",
              color: filter === f ? "#0f1117" : "#9ca3af",
              fontWeight: filter === f ? "700" : "400",
            }}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== "all" && ` (${seasons.filter(s => s.status === f).length})`}
            </button>
          ))}
        </div>
        <button onClick={() => setModal({ type: "create" })} style={{
          padding: "0.5rem 1.25rem", borderRadius: "6px", border: "none",
          background: "#f0a500", color: "#0f1117", fontWeight: "700",
          cursor: "pointer", fontSize: "0.9rem",
        }}>
          + New Season
        </button>
      </div>

      {/* Season list */}
      {loading ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
          Loading seasons...
        </div>
      ) : filteredSeasons.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
          {filter === "all"
            ? <>No seasons yet. <button onClick={() => setModal({ type: "create" })}
                style={{ background: "none", border: "none", color: "#f0a500", cursor: "pointer", fontWeight: "600" }}>
                Create your first season →
              </button></>
            : `No ${filter} seasons.`
          }
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {filteredSeasons.map(s => (
            <SeasonCard
              key={s.id}
              season={s}
              onEdit={season => setModal({ type: "edit", data: season })}
              onDelete={handleDelete}
              onAssign={season => setModal({ type: "assign", data: season })}
              onUnassign={(msg, err) => { if (err) showToast(err, "error"); else showToast(msg); loadSeasons(); }}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {modal?.type === "create" && (
        <SeasonModal onSave={handleSave} onClose={() => setModal(null)} />
      )}
      {modal?.type === "edit" && (
        <SeasonModal season={modal.data} onSave={handleSave} onClose={() => setModal(null)} />
      )}
      {modal?.type === "assign" && (
        <AssignMatchModal season={modal.data} onClose={() => setModal(null)} onDone={handleAssignDone} />
      )}
      {modal?.type === "confirm" && (
        <ConfirmDialog
          message={`Delete "${modal.data.name}"? This cannot be undone. All assigned matches will be unlinked from this season.`}
          onConfirm={confirmDelete}
          onCancel={() => setModal(null)}
        />
      )}

      </>}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
